import { ApolloClient, ApolloProvider, HttpLink, InMemoryCache, split } from '@apollo/client';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { useAuth0 } from '@auth0/auth0-react';
import {
  ErrorComponent,
  Space,
  Spin,
  notification,
  notificationProvider,
} from '@pankod/refine-antd';
import { AuthProvider, Authenticated, Refine } from '@pankod/refine-core';
import routerProvider from '@pankod/refine-react-router-v6';
import { getPolicies, model } from 'accessControl';
import 'assets/styles/antd.less';
import { MemoryAdapter, newEnforcer } from 'casbin.js';
import { Title } from 'components';
import { Header } from 'components/header';
import CustomLayout from 'components/layout/layout';
import { client, gqlDataProvider } from 'data-provider';
import { createClient } from 'graphql-ws';
import { useMobileStickyIdColumns, useResources } from 'hooks';
import jwtDecode from 'jwt-decode';
import { AccountLogs } from 'pages/accounts';
import AppSetupShow from 'pages/app-setup';
import { ImportCategories } from 'pages/categories/import';
import { ImportClients } from 'pages/clients';
import { DashboardPage } from 'pages/dashboard';
import { InventorySync } from 'pages/inventory-sync';
import { ExpenseCategoryCreate } from 'pages/expenses/create';
import { ExpenseCategoryEdit } from 'pages/expenses/edit';
import { ExpenseCategoryList } from 'pages/expenses/expense-categories';
import { ImportExpenses } from 'pages/expenses/import';
import { Login } from 'pages/login';
import { Logout } from 'pages/logout';
import { Onboarding } from 'pages/onboarding';
import OrgSetupShow from 'pages/org-setup';
import { ProductImport } from 'pages/products';
import { PosPaymentChannelsList } from 'pages/pos-payment-channels';
import { RestaurantPos } from 'pages/restaurant-pos';
import { DeliverySlipCreate } from 'pages/sales/create-delivery-slip';
import { StockTransferDeliverySlipCreate } from 'pages/stock-transfers';
import { ImportSuppliers } from 'pages/suppliers';
import { ImportTags } from 'pages/tags';
import { ImportWarehouses, WarehouseProducts } from 'pages/warehouses';
import { useEffect, useState } from 'react';
import { initializeSearch } from 'services/meilisearch';
import { getModulePermissions } from 'utilities/permission';
import { clientVersionHeaders } from 'utilities/client-version';
import './global.css';
import { ImportMasterPage } from 'pages/sales/import';

const getDefaultRouteByRole = (token: string) => {
  const decodedToken: Record<string, any> = jwtDecode(token);
  const roles: string[] = decodedToken[process.env.REACT_APP_IDENTIFIER + 'roles'] || [];
  const role = roles?.[0] ?? '';
  return role.toLowerCase() === 'staff' ? '/staff' : '/dashboard';
};

const normalizeNamespacedClaims = (claims: Record<string, any>) => {
  const normalizedClaims: Record<string, any> = { ...claims };
  Object.keys(normalizedClaims).forEach((key) => {
    if (key.includes(`${process.env.REACT_APP_IDENTIFIER}`)) {
      const newKey = key.replace(`${process.env.REACT_APP_IDENTIFIER}`, '');
      normalizedClaims[newKey] = normalizedClaims[key];
      delete normalizedClaims[key];
    }
  });
  return normalizedClaims;
};

const App: React.FC = () => {
  const [tokenReady, setTokenReady] = useState<boolean>(false);
  const { resources } = useResources();
  useMobileStickyIdColumns();
  const {
    isLoading,
    loginWithRedirect,
    isAuthenticated,
    user,
    logout,
    getIdTokenClaims,
    getAccessTokenSilently,
  } = useAuth0();

  useEffect(() => {
    const params = new URL(window.location.href).searchParams;
    const error = params.get('error');
    if (error !== 'invalid_request' && error !== 'access_denied') return;

    window.localStorage.clear();
    notification.error({
      message: 'Failed',
      description: error,
    });
    const timeoutId = window.setTimeout(() => {
      logout({ returnTo: window.location.origin });
    }, 2000);

    return () => window.clearTimeout(timeoutId);
  }, [logout]);

  useEffect(() => {
    if (!isAuthenticated) {
      setTokenReady(false);
      return;
    }

    let cancelled = false;

    const initializeAuth = async () => {
      try {
        const accessToken = await getAccessTokenSilently();
        if (!accessToken || cancelled) return;

        window.localStorage.setItem('token', accessToken);
        client.setHeader('authorization', `Bearer ${accessToken}`);
        setTokenReady(true);
        await initializeSearch();

        const idTokenClaims = await getIdTokenClaims();
        if (!idTokenClaims || cancelled) return;

        const normalizedClaims = normalizeNamespacedClaims(idTokenClaims as Record<string, any>);
        window.localStorage.setItem('user', JSON.stringify(normalizedClaims));
      } catch (err: any) {
        if (cancelled) return;
        setTokenReady(false);
        notification.error({
          message: 'Authentication Failed',
          description: err?.message || 'Could not initialize session.',
        });
      }
    };

    initializeAuth();

    return () => {
      cancelled = true;
    };
  }, [getAccessTokenSilently, getIdTokenClaims, isAuthenticated]);

  // Redirect after token is available,
  useEffect(() => {
    const isFirstLogin = !localStorage.getItem('hasLoggedIn');
    if (!isAuthenticated || !tokenReady || !isFirstLogin) return;

    const token = localStorage.getItem('token');
    if (!token) return;

    const redirectionPath = getDefaultRouteByRole(token);

    localStorage.setItem('hasLoggedIn', 'true');

    if (window.location.pathname !== redirectionPath) {
      window.history.replaceState(window.history.state, '', redirectionPath);
      window.dispatchEvent(new Event('popstate'));
    }
  }, [isAuthenticated, tokenReady]);

  const role = 'user';

  const getAdapter = () => {
    if (!isAuthenticated) {
      return '';
    }

    const token = window.localStorage.getItem('token');
    if (!token) {
      return '';
    }

    return getPolicies(token);
  };

  const authProvider: AuthProvider = {
    login: () => {
      return loginWithRedirect();
    },

    logout: () => {
      localStorage.clear();
      logout({ returnTo: window.location.origin });
      return Promise.resolve('/');
    },

    checkError: () => Promise.resolve(),

    checkAuth: () => {
      if (isAuthenticated) {
        return Promise.resolve();
      }
      return Promise.reject();
    },

    getPermissions: async () => {
      /*
      const adapter = new MemoryAdapter(getAdapter());
      const enforcer = await newEnforcer(model, adapter);
      const permissions = await enforcer.getPolicy();
      const permissionData = permissions.map((permission) => ({
        role: permission[0],
        resource: permission[1],
        actions: permission.slice(2).join(','),
      }));
      const permissionObject = transformToPermissionsObject(permissionData);
      return permissionObject;
      */

      const token = window.localStorage.getItem('token');
      if (!token) return;

      const decodedToken: Record<string, any> = jwtDecode(token);
      const permissions: string[] =
        decodedToken[process.env.REACT_APP_IDENTIFIER + 'permissions'] || [];
      const modulePermissions = getModulePermissions(permissions);

      return modulePermissions;
    },

    getUserIdentity: async () => {
      if (user) {
        return Promise.resolve({
          ...user,
          avatar: user.picture,
        });
      }
    },
  };

  if (isLoading) {
    return (
      <Space
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <Spin size="large" tip="Loading..." />
      </Space>
    );
  }

  // Function to Retrieve Token
  const getAuthToken = () => {
    return localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : '';
  };

  // Create WebSocket Link for GraphQL Subscriptions
  const wsLink = new GraphQLWsLink(
    createClient({
      url: process.env.REACT_APP_API_URI!.replace('http', 'ws'), // Convert HTTP to WS
      connectionParams: {
        authorization: getAuthToken(),
        ...clientVersionHeaders,
      },
    }),
  );

  // Create HTTP Link for Queries & Mutations
  const httpLink = new HttpLink({
    uri: process.env.REACT_APP_API_URI!,
    headers: {
      authorization: getAuthToken(),
      ...clientVersionHeaders,
    },
  });

  // Use WebSocket for Subscriptions & HTTP for Queries/Mutations Dynamically
  const splitLink = split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
    },
    wsLink, // Use WebSocket link for subscriptions
    httpLink, // Use HTTP link for queries & mutations
  );

  // Initialize Apollo Client
  const apolloClient = new ApolloClient({
    link: splitLink,
    cache: new InMemoryCache(),
  });

  return (
    <ApolloProvider client={apolloClient}>
      <Refine
        dataProvider={gqlDataProvider}
        authProvider={authProvider}
        LoginPage={Login}
        Title={Title}
        Header={Header}
        DashboardPage={DashboardPage}
        notificationProvider={notificationProvider}
        Layout={CustomLayout}
        catchAll={<ErrorComponent />}
        accessControlProvider={{
          can: async ({ action, params, resource }) => {
            const adapter = new MemoryAdapter(getAdapter());
            const enforcer = await newEnforcer(model, adapter);
            if (params && (action === 'delete' || action === 'edit' || action === 'show')) {
              return Promise.resolve({
                can: await enforcer.enforce(role, `${resource}/${params.id}`, action),
              });
            }
            if (params && action === 'field') {
              return Promise.resolve({
                can: await enforcer.enforce(role, `${resource}/${params.field}`, action),
              });
            }
            return Promise.resolve({
              can: await enforcer.enforce(role, resource, action),
            });
          },
        }}
        routerProvider={{
          ...routerProvider,
          routes: [
            {
              path: '/onboarding',
              layout: false,
              element: <Onboarding />,
            },
            {
              path: '/onboarding/user',
              layout: false,
              element: <Onboarding />,
            },
            {
              path: '/products/import',
              layout: true,
              element: (
                <Authenticated>
                  <ProductImport />
                </Authenticated>
              ),
            },
            {
              path: '/clients/import',
              layout: true,
              element: (
                <Authenticated>
                  <ImportClients />
                </Authenticated>
              ),
            },
            {
              path: '/suppliers/import',
              layout: true,
              element: (
                <Authenticated>
                  <ImportSuppliers />
                </Authenticated>
              ),
            },
            {
              path: '/tags/import',
              layout: true,
              element: (
                <Authenticated>
                  <ImportTags />
                </Authenticated>
              ),
            },
            {
              path: '/categories/import',
              layout: true,
              element: (
                <Authenticated>
                  <ImportCategories />
                </Authenticated>
              ),
            },
            {
              path: '/warehouses/import',
              layout: true,
              element: (
                <Authenticated>
                  <ImportWarehouses />
                </Authenticated>
              ),
            },
            {
              path: '/import',
              layout: true,
              element: (
                <Authenticated>
                  <ImportMasterPage />
                </Authenticated>
              ),
            },
            {
              path: '/warehouses/:warehouseId/products',
              layout: true,
              element: (
                <Authenticated>
                  <WarehouseProducts />
                </Authenticated>
              ),
            },
            {
              path: '/accounts/show/:accountType/:accountTypeId',
              layout: true,
              element: (
                <Authenticated>
                  <AccountLogs />
                </Authenticated>
              ),
            },
            {
              path: '/expenses/expense-categories',
              layout: true,
              element: (
                <Authenticated>
                  <ExpenseCategoryList />
                </Authenticated>
              ),
            },
            {
              path: '/expenses/expense-categories/create',
              layout: true,
              element: (
                <Authenticated>
                  <ExpenseCategoryCreate />
                </Authenticated>
              ),
            },
            {
              path: '/expenses/expense-categories/edit/:editId',
              layout: true,
              element: (
                <Authenticated>
                  <ExpenseCategoryEdit />
                </Authenticated>
              ),
            },
            {
              path: '/expenses/import',
              layout: true,
              element: (
                <Authenticated>
                  <ImportExpenses />
                </Authenticated>
              ),
            },
            {
              path: '/organizations',
              layout: true,
              element: (
                <Authenticated>
                  <OrgSetupShow />
                </Authenticated>
              ),
            },
            {
              path: '/apps',
              layout: true,
              element: (
                <Authenticated>
                  <AppSetupShow />
                </Authenticated>
              ),
            },
            {
              path: '/logout',
              layout: true,
              element: <Logout />,
            },
            {
              path: '/restaurant-pos',
              layout: true,
              element: (
                <Authenticated>
                  <RestaurantPos />
                </Authenticated>
              ),
            },
            {
              path: '/pos-payment-channels',
              layout: true,
              element: (
                <Authenticated>
                  <PosPaymentChannelsList />
                </Authenticated>
              ),
            },
            {
              // Internal tooling: not registered as a resource, so it has no sidebar entry.
              // Reachable only by navigating directly to /inventory-sync.
              path: '/inventory-sync',
              layout: false,
              element: (
                <Authenticated>
                  <InventorySync />
                </Authenticated>
              ),
            },
            {
              path: '/sales/show/:id/delivery-slip/create',
              layout: true,
              element: (
                <Authenticated>
                  <DeliverySlipCreate />
                </Authenticated>
              ),
            },
            {
              path: '/stock-transfers/show/:id/delivery-slip/create',
              layout: true,
              element: (
                <Authenticated>
                  <StockTransferDeliverySlipCreate />
                </Authenticated>
              ),
            },
          ],
        }}
        resources={resources}
      />
    </ApolloProvider>
  );
};

export default App;
// comment 2
